import { useEffect, useState } from 'react';
import { Gauge, Layers, Star } from 'lucide-react';
import { UncategorizedIcon } from '@/shared/config';
import {
  kpiApi,
  type KpiComplexityLevel,
  type KpiSatisfactionLevel,
  type KpiSettings,
} from '@/shared/api';
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui';
import styles from './KpiLeaderboard.module.css';

const SPEED_TIERS = [
  { label: 'Быстрее половины целевого срока', value: '×1.5' },
  { label: 'В пределах целевого срока', value: '×1' },
  { label: 'До двух целевых сроков', value: '×0.75' },
  { label: 'Дольше двух сроков', value: '×0.5' },
];

export interface ScoringDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ScoringDialog: React.FC<ScoringDialogProps> = ({ open, onOpenChange }) => {
  const [complexity, setComplexity] = useState<KpiComplexityLevel[]>([]);
  const [satisfaction, setSatisfaction] = useState<KpiSatisfactionLevel[]>([]);
  const [settings, setSettings] = useState<KpiSettings | null>(null);

  useEffect(() => {
    if (!open) return;
    kpiApi
      .complexityLevels()
      .then((res) => setComplexity(res.data ?? []))
      .catch(() => setComplexity([]));
    kpiApi
      .satisfactionLevels()
      .then((res) => setSatisfaction(res.data ?? []))
      .catch(() => setSatisfaction([]));
    kpiApi
      .settings()
      .then((res) => setSettings(res.data ?? null))
      .catch(() => setSettings(null));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ maxWidth: 720 }}>
        <DialogTitle>Как считаются очки</DialogTitle>

        <div className={styles.scoringFormula}>
          <span className={styles.scoringTerm}>база категории</span>
          <span className={styles.scoringOp}>×</span>
          <span className={styles.scoringTerm}>скорость</span>
          <span className={styles.scoringOp}>×</span>
          <span className={styles.scoringTerm}>сложность</span>
          <span className={styles.scoringOp}>÷</span>
          <span className={styles.scoringTerm}>число исполнителей</span>
        </div>
        <p className={styles.scoringNote}>
          Очки начисляются в момент решения заявки. Оценка заявителя приходит позже и
          применяется отдельной корректирующей строкой — поэтому в детализации она видна
          отдельно от начисления.
        </p>

        <div className={styles.scoringGrid}>
          <div className={styles.scoringBlock}>
            <h3 className={styles.scoringHeading}>
              <Gauge size={15} aria-hidden />
              Скорость решения
            </h3>
            <ul className={styles.scoringList}>
              {SPEED_TIERS.map((t) => (
                <li key={t.value}>
                  <span>{t.label}</span>
                  <b>{t.value}</b>
                </li>
              ))}
            </ul>
            <p className={styles.scoringNote}>
              Срок считается от создания заявки до решения; целевое время задаётся в
              категории. У категории без базовых очков начислений нет.
            </p>
          </div>

          <div className={styles.scoringBlock}>
            <h3 className={styles.scoringHeading}>
              <Layers size={15} aria-hidden />
              Классы сложности
            </h3>
            {complexity.length > 0 ? (
              <ul className={styles.scoringList}>
                {complexity.map((l) => (
                  <li key={l.class}>
                    <span>{l.label}</span>
                    <b>×{l.multiplier}</b>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.scoringNote}>Классы сложности не настроены</p>
            )}
            <p className={styles.scoringNote}>
              Класс ставит старший инженер; исполнитель может запросить пересмотр до решения
              заявки. При переоткрытии заявки очки сторнируются.
            </p>
          </div>

          <div className={styles.scoringBlock}>
            <h3 className={styles.scoringHeading}>
              <Star size={15} aria-hidden />
              Оценка заявителя
            </h3>
            {satisfaction.length > 0 ? (
              <ul className={styles.scoringList}>
                {satisfaction.map((l) => (
                  <li key={l.rating}>
                    <span>
                      {'★'.repeat(l.rating)}
                      {'☆'.repeat(5 - l.rating)} {l.label}
                    </span>
                    <b>×{l.multiplier}</b>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.scoringNote}>Множители оценки не настроены</p>
            )}
            <p className={styles.scoringNote}>
              Оценку ставит заявитель при подтверждении выполнения. Смена множителя не
              пересчитывает уже начисленное.
            </p>
          </div>

          <div className={styles.scoringBlock}>
            <h3 className={styles.scoringHeading}>
              <UncategorizedIcon size={15} aria-hidden />
              Заявки без категории
            </h3>
            {settings ? (
              <ul className={styles.scoringList}>
                <li>
                  <span>База</span>
                  <b>
                    {settings.defaultBasePoints === 0
                      ? 'не начислять'
                      : settings.defaultBasePoints}
                  </b>
                </li>
                <li>
                  <span>Целевое время</span>
                  <b>
                    {settings.defaultTargetMinutes == null
                      ? 'по сроку SLA'
                      : `${settings.defaultTargetMinutes} мин`}
                  </b>
                </li>
              </ul>
            ) : (
              <p className={styles.scoringNote}>Величины недоступны</p>
            )}
            <p className={styles.scoringNote}>
              Заявку можно подать в свободной форме — категории у неё нет, и работают эти
              величины. У остальных заявок — настройки их категории, снятые при подаче.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
